import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { AlertTriangle } from 'lucide-react';

/**
 * ConfirmProvider – Stellt globale `confirm()` und `prompt()` Funktionen bereit,
 * die schöne Shadcn-Dialoge statt `window.confirm/prompt` nutzen.
 *
 * Verwendung:
 *   const { confirm, prompt } = useConfirm();
 *   const ok = await confirm({ title: 'Wirklich löschen?', description: '...' });
 *   const val = await prompt({ title: 'Datum wählen', type: 'date', defaultValue: '2026-01-01' });
 */
const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
  const [confirmState, setConfirmState] = useState(null); // { title, description, resolve, ... }
  const [promptState, setPromptState] = useState(null); // { title, description, defaultValue, resolve, type }
  const [promptValue, setPromptValue] = useState('');
  const inputRef = useRef(null);

  const confirm = useCallback((opts = {}) => {
    return new Promise((resolve) => {
      setConfirmState({
        title: opts.title || 'Bestätigen?',
        description: opts.description || '',
        confirmLabel: opts.confirmLabel || 'Bestätigen',
        cancelLabel: opts.cancelLabel || 'Abbrechen',
        variant: opts.variant || 'default',
        resolve,
      });
    });
  }, []);

  const prompt = useCallback((opts = {}) => {
    return new Promise((resolve) => {
      setPromptValue(opts.defaultValue ?? '');
      setPromptState({
        title: opts.title || 'Eingabe',
        description: opts.description || '',
        label: opts.label || 'Wert',
        placeholder: opts.placeholder || '',
        confirmLabel: opts.confirmLabel || 'Bestätigen',
        cancelLabel: opts.cancelLabel || 'Abbrechen',
        type: opts.type || 'text',
        resolve,
      });
      setTimeout(() => inputRef.current?.focus(), 100);
    });
  }, []);

  const handleConfirm = (result) => {
    confirmState?.resolve(result);
    setConfirmState(null);
  };
  const handlePrompt = (submit) => {
    if (submit) {
      const val = promptValue.trim();
      promptState?.resolve(val || null);
    } else {
      promptState?.resolve(null);
    }
    setPromptState(null);
    setPromptValue('');
  };

  return (
    <ConfirmContext.Provider value={{ confirm, prompt }}>
      {children}
      <AlertDialog open={!!confirmState} onOpenChange={(v) => !v && handleConfirm(false)}>
        <AlertDialogContent data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              {confirmState?.variant === 'destructive' && <AlertTriangle size={18} className="text-rose-600"/>}
              {confirmState?.title}
            </AlertDialogTitle>
            {confirmState?.description && (
              <AlertDialogDescription>{confirmState.description}</AlertDialogDescription>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => handleConfirm(false)} data-testid="confirm-cancel">
              {confirmState?.cancelLabel}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleConfirm(true)}
              className={confirmState?.variant === 'destructive' ? 'bg-rose-600 hover:bg-rose-700 text-white' : ''}
              data-testid="confirm-ok"
            >
              {confirmState?.confirmLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <Dialog open={!!promptState} onOpenChange={(v) => !v && handlePrompt(false)}>
        <DialogContent data-testid="prompt-dialog">
          <DialogHeader>
            <DialogTitle>{promptState?.title}</DialogTitle>
            {promptState?.description && (
              <DialogDescription>{promptState.description}</DialogDescription>
            )}
          </DialogHeader>
          <div className="space-y-2">
            {promptState?.label && <Label>{promptState.label}</Label>}
            <Input
              ref={inputRef}
              type={promptState?.type || 'text'}
              value={promptValue}
              onChange={e => setPromptValue(e.target.value)}
              placeholder={promptState?.placeholder}
              onKeyDown={e => {
                if (e.key === 'Enter') handlePrompt(true);
                else if (e.key === 'Escape') handlePrompt(false);
              }}
              data-testid="prompt-input"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => handlePrompt(false)} data-testid="prompt-cancel">
              {promptState?.cancelLabel}
            </Button>
            <Button onClick={() => handlePrompt(true)} data-testid="prompt-ok">
              {promptState?.confirmLabel}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ConfirmContext.Provider>
  );
}

export const useConfirm = () => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm muss innerhalb ConfirmProvider verwendet werden.');
  return ctx;
};

/**
 * ConfirmButton – Convenience-Component: Button, der beim Klick zunächst confirm() zeigt.
 */
export function ConfirmButton({
  onConfirm, title, description, confirmLabel = 'Bestätigen', cancelLabel = 'Abbrechen',
  variant = 'default', buttonVariant = 'default', children, disabled, className, ...rest
}) {
  const { confirm } = useConfirm();
  const handleClick = async () => {
    const ok = await confirm({ title, description, confirmLabel, cancelLabel, variant });
    if (ok) await onConfirm?.();
  };
  return (
    <Button
      variant={buttonVariant}
      onClick={handleClick}
      disabled={disabled}
      className={className}
      {...rest}
    >
      {children}
    </Button>
  );
}
